/**
 * Versioned, immutable wording for the cost-basis line and the ESID help text.
 * Source: "AIOpCo Docs & Templates/Cost Basis And ESID Wording.md" (Witherspoon-CLO, 2026-09-20), Sections 1 and 2;
 * the bill-credit notice is from compliance/01_texas_disclosures.md Section 5 (v2.1).
 *
 * STATUS: DRAFT. Not approved by outside counsel. The items in OPEN_ITEMS below are still open (VERIFY = a fact CLO
 * cannot confirm without the live rule text or a live bill; ATTORNEY = a question for outside counsel) and stay
 * flagged here until they are closed. Nothing on a screen calls this text approved.
 *
 * Rules: never edit a published version in place, add a new id. Nobody rewords these in a component: components
 * render these constants. Ive-WBD may restyle; the words are CLO's. The words "Estimated", "Facts Label" and "may
 * differ" must stay in every cost-basis variant, and none of the banned words may appear near a price (a test checks).
 */

export const TEXT_STATUS = "DRAFT";

export const OPEN_ITEMS = [
  { tag: "VERIFY", text: "16 TAC 25.475(g): the EFL average price includes the base charge (and which delivery charges). The whole 'no monthly fee is added' sentence rests on it.", owner: "Witherspoon-CLO" },
  { tag: "VERIFY", text: "16 TAC 25.475 current text and effective date (last confirmed as of 01/06/2022).", owner: "Witherspoon-CLO" },
  { tag: "VERIFY", text: "Bills label the identifier 'ESI ID'. Delete that phrase from the ESID help text if suppliers use other labels.", owner: "Martha-BRM with a live bill sample" },
  { tag: "VERIFY", text: "Which suppliers require an ESID on enrollment; it sets the required-or-optional word per connector.", owner: "Torvalds-BE" },
  { tag: "ATTORNEY", text: "Whether 25.475 display rules reach a broker's or aggregator's estimate line.", owner: "Outside counsel" },
  { tag: "ATTORNEY", text: "Whether collecting an ESID adds a privacy notice or consent duty (25.486(j)).", owner: "Outside counsel" },
];

// Words that must not appear near a price (CLO Section 1 rules).
export const BANNED_NEAR_PRICE = ["true", "exact", "guaranteed", "total cost", "cheapest", "lowest"];

export const COST_BASIS = {
  // 2026-09-30: Owner asked for "Price includes all Energy Charges + Delivery Charges. Excludes taxes." verbatim.
  // Not shipped verbatim -- see CLO note below and the handback report for the full reasoning. This version keeps
  // the Owner's clearer includes/excludes framing but (a) restores the three fixed words the header comment and
  // the test suite require in every variant ("Estimated"/"Estimate", "Facts Label", "may differ" -- the Owner's
  // sentence had none of them), and (b) names the base/monthly charge explicitly rather than folding it silently
  // into "Energy Charges": whether the EFL's average price includes the base charge is still an open, unconfirmed
  // VERIFY item (see OPEN_ITEMS, 16 TAC 25.475(g)), so this text keeps the same level of commitment the prior
  // draft already made (base charge included) instead of a broader claim no source here confirms. "Excludes
  // taxes" is added because every source reviewed (compliance/01_texas_disclosures.md, 06_verification_report.md)
  // is consistent with taxes never being part of the EFL average-price calculation, for any plan or customer
  // class; no source found a counterexample.
  id: "cost_basis-2026-09-30-draft2",
  status: TEXT_STATUS,
  // {link} marks where the Facts Label link goes; without a usable https URL it is left out.
  primary: {
    template:
      "Estimated monthly cost = the average price per kWh on this plan's Electricity Facts Label at this usage level, times the kWh shown. The label's average price already includes the energy charge, the delivery charges from your local utility, and the base charge, so no separate monthly fee is added. It does not include taxes. Your actual bill may differ. {link}",
    linkText: "View Electricity Facts Label",
  },
  // Plan cards only (CLO Section 1, Shorter Variant).
  short: {
    template: "Estimate: Facts Label average price times usage, already including energy, delivery, and base charges, but not taxes. Your bill may differ. {link}",
    linkText: "Facts Label",
  },
};

// Shown directly beneath the cost-basis line for a plan with bill credits or usage-conditional pricing
// (compliance/01_texas_disclosures.md Section 5, v2.1).
export const BILL_CREDIT_NOTICE = {
  id: "bill_credit_notice-2026-09-20-draft1",
  status: TEXT_STATUS,
  lead: "Bill credit plan.",
  text: "The advertised price depends on credits that apply only if your monthly usage is within the range stated in the EFL. If your usage is outside that range, you may pay more. Check the EFL before you enroll.",
};

export const ESID_HELP = {
  id: "esid_help-2026-09-25-draft2",
  status: TEXT_STATUS,
  heading: "Where To Find Your ESID",
  // Reissued 2026-09-25: the ERCOT-backed address lookup now returns real results (Technical Blueprint
  // Section 26), so the prior "RateCurrent cannot look it up for you" sentence is false and is retired here,
  // not edited in place -- see the id bump. Still no example number, no digits, no format hint until the
  // format is verified.
  primary:
    "Your ESID (Electric Service Identifier) is the number that identifies your home's electric meter. RateCurrent's ESID lookup tool can usually find it for you automatically from your service address. If it can't confirm a match for your address, you can find your ESID printed on your electric bill; it may be labeled \"ESI ID.\"",
  // Beside the ESID field. The requirement word comes from the connector's form configuration, never hard-coded.
  // Unchanged from draft1: this line never made the false "cannot look it up" claim, so there is nothing to correct.
  short: "Find your ESID on your electric bill. This field is {requirement} for this plan.",
};

export const isHttps = (u) => typeof u === "string" && /^https:\/\//i.test(u.trim());

/**
 * Plan-level disclosure block (compliance/01_texas_disclosures.md Section 5). Rendered next to every price list
 * and on the plan detail page, above and below the results list. The template omits the "{{rate_effective_date}}"
 * placeholder when no per-plan date is available rather than showing an unresolved token; the tier list is passed
 * in so a future small-commercial tier set (1,500/2,500/3,500 kWh) does not require new copy.
 */
export const PLAN_DISCLOSURE_BLOCK = {
  id: "plan_disclosure_block-2026-09-22-draft1",
  status: TEXT_STATUS,
  intro: "Prices shown are the provider's average price per kWh at 500, 1,000, and 2,000 kWh of monthly use, taken from the provider's Electricity Facts Label (EFL).",
  controls: "The EFL is the provider's official disclosure. If anything on this site differs from the EFL, the EFL controls.",
  footer: "Your actual bill depends on your usage, taxes, fees, changes to your local utility's delivery charges, and any bill-credit or usage conditions. Estimated monthly costs are calculated by RateCurrent from the provider's published prices and the usage you enter. They are estimates, not offers or guarantees.",
};

/**
 * "How we are paid" / "What we show" (compliance/01_texas_disclosures.md Section 3). This text is already
 * live on pages/HowItWorks.jsx; it is centralized here so Plans, Plan Detail, and Enroll render the identical,
 * already-reviewed wording instead of a second draft of the same disclosure.
 */
export const COMPENSATION_DISCLOSURE = {
  id: "compensation_disclosure-2026-09-20-draft1",
  status: TEXT_STATUS,
  heading: "How we are paid",
  text: "RateCurrent may be paid by energy providers when you enroll in a plan through our site. This payment does not change the price you pay. It may influence which providers and plans we are able to show you.",
};

export const SCOPE_DISCLOSURE = {
  id: "scope_disclosure-2026-09-20-draft1",
  status: TEXT_STATUS,
  heading: "What we show",
  text: "We do not show every plan or every provider available at your address. Plans and prices on this site come from information our team collects from each provider's published disclosure documents. Your state may have an official comparison website that lists other offers.",
};

/** compliance/01_texas_disclosures.md Section 6, non-provider-specific lead sentence only (the rest of Section 6
 * needs {{PROVIDER_NAME}} / {{TDU_NAME}} filled from real connector data, added where the enrollment config
 * supplies them). */
export const WHAT_HAPPENS_WHEN_YOU_ENROLL_LEAD =
  "Your switch may not take effect immediately, and your provider will confirm your start date. RateCurrent does not take payments; your provider, not RateCurrent, supplies your service, sends your bills, and is your contract counterparty.";

/**
 * Cost-basis line as parts the UI renders: [{ text }, { text, href }]. `variant` is "primary" or "short".
 * The link goes to the plan's Facts Label (the plan's EFL URL); a plan with no https link shows the sentence without
 * a link, never a dead one. `plain` is the whole sentence, for tests and logs.
 */
export function renderCostBasis(variant, { eflUrl } = {}) {
  const def = COST_BASIS[variant];
  if (!def) throw new Error(`Unknown cost basis variant: ${variant}`);
  const href = isHttps(eflUrl) ? eflUrl.trim() : undefined;
  const parts = [];
  def.template.split(/(\{link\})/).forEach((chunk) => {
    if (chunk === "{link}") { if (href) parts.push({ text: def.linkText, href }); }
    else if (chunk) parts.push({ text: chunk });
  });
  return { versionId: COST_BASIS.id, status: TEXT_STATUS, variant, parts, plain: parts.map((p) => p.text).join("").trim() };
}

/** Text beside the ESID field. `required` is the field's own flag from the connector's form configuration. */
export function esidFieldHelp(required) {
  return ESID_HELP.short.replace("{requirement}", required ? "required" : "optional");
}

/** Does this extension field hold the ESID? Matches the connector field name, case-insensitively. */
export const isEsidField = (field) => !!field && /^(esid|esi_id)$/i.test(String(field.name || ""));
